import { useMemo, type ComponentType, type SVGProps } from 'react';
import { HUBSPOT_UI_ENABLED } from '@/lib/hubspotUi';
import { getInitials } from '@/utils/initials';
import { formatDate, formatDurationWords, formatRelativeTime } from '@/i18n/locale';
import { useTranslation } from 'react-i18next';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Clock,
  Calendar,
  Voicemail,
} from 'lucide-react';

type IconType = ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;

interface OutcomeMeta {
  Icon: IconType;
  iconBg: string;
  iconColor: string;
}

interface CallShape {
  id?: string;
  prospect_name?: string | null;
  prospect_company?: string | null;
  prospect_phone?: string | null;
  call_outcome?: string | null;
  call_status?: string | null;
  duration_seconds?: number | null;
  connected_at?: string | Date | null;
  completed_at?: string | Date | null;
  started_at?: string | Date | null;
  created_date?: string | Date | null;
  hubspot_contact_id?: string | null;
  hubspot_activity_id?: string | null;
}

interface ProspectShape {
  prospect_name?: string | null;
  prospect_phone?: string | null;
  prospect_company?: string | null;
}

interface FeedEntryData {
  key: string;
  call: CallShape;
  meta: OutcomeMeta;
  label: string;
  name: string;
  company: string | null;
  ts: string | Date;
  duration: number | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_EVENTS = 50;

// ---------------------------------------------------------------------------
// Outcome metadata — drives icon, colour, and label per call
// ---------------------------------------------------------------------------

const OUTCOME_META: Record<string, OutcomeMeta> = {
  meeting_booked: {
    Icon: Calendar,
    iconBg: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'text-green-600 dark:text-green-400',
    },
  contacted: {
    Icon: PhoneIncoming,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
    },
  callback_requested: {
    Icon: Clock,
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    iconColor: 'text-amber-600 dark:text-amber-400',
    },
  callback_scheduled: {
    Icon: Clock,
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    iconColor: 'text-amber-600 dark:text-amber-400',
    },
  not_interested: {
    Icon: PhoneMissed,
    iconBg: 'bg-muted',
    iconColor: 'text-muted-foreground',
    },
  not_reachable: {
    Icon: PhoneMissed,
    iconBg: 'bg-muted',
    iconColor: 'text-muted-foreground',
    },
  no_answer: {
    Icon: PhoneMissed,
    iconBg: 'bg-muted',
    iconColor: 'text-muted-foreground',
    },
  voicemail: {
    Icon: Voicemail,
    iconBg: 'bg-muted',
    iconColor: 'text-muted-foreground',
    },
};

// Status-level fallback (used when call_outcome is absent/unrecognised)
const STATUS_META: Record<string, OutcomeMeta> = {
  voicemail: {
    Icon: Voicemail,
    iconBg: 'bg-muted',
    iconColor: 'text-muted-foreground',
    },
  no_answer: {
    Icon: PhoneMissed,
    iconBg: 'bg-muted',
    iconColor: 'text-muted-foreground',
    },
  failed: {
    Icon: PhoneMissed,
    iconBg: 'bg-muted',
    iconColor: 'text-muted-foreground',
    },
  completed: {
    Icon: PhoneOutgoing,
    iconBg: 'bg-muted',
    iconColor: 'text-muted-foreground',
    },
};

// Generic fallback when nothing matches
const DEFAULT_META: OutcomeMeta = {
  Icon: Phone,
  iconBg: 'bg-muted',
  iconColor: 'text-muted-foreground',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns the best matching display metadata for a call record.
 */
function resolveCallMeta(call: CallShape): OutcomeMeta {
  if (call.call_outcome && OUTCOME_META[call.call_outcome]) {
    return OUTCOME_META[call.call_outcome]!;
  }
  if (call.call_status && STATUS_META[call.call_status]) {
    return STATUS_META[call.call_status]!;
  }
  return DEFAULT_META;
}

/**
 * Returns a human-readable relative timestamp in French.
 */

/**
 * Derives 1-2 uppercase initials from a name string.
 */

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Avatar({ name }: { name: string | null | undefined }) {
  const initials = getInitials(name);
  return (
    <div
      className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center ring-2 ring-card z-10"
      aria-hidden
    >
      <span className="text-xs font-semibold text-primary leading-none">{initials}</span>
    </div>
  );
}

function OutcomeBadge({ meta, label }: { meta: OutcomeMeta; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full font-medium
        ${meta.iconBg} ${meta.iconColor}`}
    >
      <meta.Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

function HubSpotBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-xs text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400 px-1.5 py-0.5 rounded-full font-medium">
      <span className="w-1.5 h-1.5 rounded-full bg-orange-500 inline-block" />
      HubSpot
    </span>
  );
}

function FeedEntry({ call, meta, label, name, company, ts, duration }: FeedEntryData) {
  const formattedDuration = formatDurationWords(duration);

  return (
    <div className="flex gap-3 group">
      {/* Avatar initials */}
      <Avatar name={name} />

      {/* Content */}
      <div className="flex-1 min-w-0 pb-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {/* Name + company */}
            <p className="text-sm font-semibold text-foreground leading-tight truncate">
              {name}
              {company && (
                <span className="text-muted-foreground font-normal"> · {company}</span>
              )}
            </p>

            {/* Outcome badge + duration on second line */}
            <div className="flex items-center flex-wrap gap-1.5 mt-1">
              <OutcomeBadge meta={meta} label={label} />
              {formattedDuration && (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  {formattedDuration}
                </span>
              )}
              {HUBSPOT_UI_ENABLED && !!(call.hubspot_contact_id || call.hubspot_activity_id) && (
                <HubSpotBadge />
              )}
            </div>
          </div>

          {/* Relative timestamp */}
          <span className="flex-shrink-0 text-xs text-muted-foreground/60 whitespace-nowrap mt-0.5">
            {formatRelativeTime(ts)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface CRMActivityFeedProps {
  allCalls?: CallShape[];
  prospects?: ProspectShape[];
}

export default function CRMActivityFeed({ allCalls = [], prospects = [] }: CRMActivityFeedProps) {
  const { t } = useTranslation('dialer');
  const prospectsById = useMemo(() => {
    const map: Record<string, ProspectShape> = {};
    prospects.forEach((p) => {
      if (p.prospect_phone) map[p.prospect_phone] = p;
    });
    return map;
  }, [prospects]);

  const entries = useMemo<FeedEntryData[]>(() => {
    const enriched = allCalls
      .map((call): FeedEntryData | null => {
        const ts = call.connected_at || call.completed_at || call.started_at || call.created_date;
        if (!ts) return null;

        const prospect = call.prospect_phone ? prospectsById[call.prospect_phone] : undefined;
        const name = call.prospect_name || prospect?.prospect_name || 'Contact inconnu';
        const company = call.prospect_company || prospect?.prospect_company || null;
        const meta = resolveCallMeta(call);
        const label = outcomeLabel(call.call_outcome || call.call_status, t);

        return {
          key: call.id || `${call.prospect_phone}_${ts}`,
          call,
          meta,
          label,
          name,
          company,
          ts,
          duration: call.duration_seconds ?? null,
        };
      })
      .filter((e): e is FeedEntryData => e !== null);

    const seen = new Set<string>();
    const unique: FeedEntryData[] = [];
    for (const entry of enriched) {
      if (!seen.has(entry.key)) {
        seen.add(entry.key);
        unique.push(entry);
      }
    }

    return unique
      .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
      .slice(0, MAX_EVENTS);
  }, [allCalls, prospectsById]);

  // Empty state — never show fake data
  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Phone className="w-8 h-8 mb-3 text-muted-foreground/30" />
        <p className="text-sm font-medium text-muted-foreground/60">{t('dialer:crmActivityFeed.noRecentActivity')}</p>
        <p className="text-xs text-muted-foreground/40 mt-1">
          {t('dialer:crmActivityFeed.interactionsWillAppear')}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl">
      {/* Vertical timeline */}
      <div className="relative pl-4">
        {/* Continuous guide line behind the avatars */}
        <div
          className="absolute left-[1.75rem] top-4 bottom-4 w-px bg-border"
          aria-hidden
        />

        <div className="flex flex-col gap-0">
          {entries.map(entry => (
            <FeedEntry
              key={entry.key}
              call={entry.call}
              meta={entry.meta}
              label={entry.label}
              name={entry.name}
              company={entry.company}
              ts={entry.ts}
              duration={entry.duration}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
